import { useForm } from '@tanstack/react-form';
import type { AppRole } from '@tronche2tek/shared-types';
import { isOneOf, VALID_ROLES } from '@tronche2tek/shared-types';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { AppUserSortField, AppUsersPage } from '../../api/admin';
import { UserSearchCombobox } from '../users/UserSearchCombobox';
import { ConfirmAdminDialog } from './ConfirmAdminDialog';
import { LoadingSpinner } from './shared';
import { fmt } from './utils';

/** The role <select>s only offer '' (none) and the three roles. */
function roleValue(raw: string): AppRole | null {
  return isOneOf(VALID_ROLES, raw) ? raw : null;
}

export type UsersQuery = {
  page: number;
  q: string;
  sortBy: AppUserSortField;
  sortDir: 'asc' | 'desc';
};

function RoleOptions() {
  const { t } = useTranslation();
  return (
    <>
      <option value="">{t('admin.users.roleNone')}</option>
      <option value="user">{t('admin.users.roleUser')}</option>
      <option value="operator">{t('admin.users.roleOperator')}</option>
      <option value="admin">{t('admin.users.roleAdmin')}</option>
    </>
  );
}

/** The add-user fields, as typed: the role is the raw <select> string. */
interface AddUserFields {
  upn: string;
  role: string;
}

function initialAddUserFields(): AddUserFields {
  return { upn: '', role: '' };
}

function AddUserForm({
  onCreate,
  isCreating,
  onClose,
}: {
  onCreate: (body: { upn: string; role?: AppRole | null }) => Promise<unknown>;
  isCreating: boolean;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const [confirmAdmin, setConfirmAdmin] = useState(false);

  const create = async (value: AddUserFields) => {
    const trimmed = value.upn.trim();
    if (!trimmed || isCreating) {
      return;
    }
    try {
      await onCreate({ upn: trimmed, role: roleValue(value.role) });
      onClose();
    } catch {
      // error is surfaced by the page-level ErrorMessage
    }
  };

  const form = useForm({
    defaultValues: initialAddUserFields(),
    onSubmit: async ({ value }) => {
      // Creating an admin goes through the warning dialog first.
      if (roleValue(value.role) === 'admin') {
        setConfirmAdmin(true);
        return;
      }
      await create(value);
    },
  });

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        void form.handleSubmit();
      }}
      className="mb-5 space-y-3 border border-rule bg-blue/4 p-5"
    >
      <h4 className="text-sm font-semibold text-ink">
        {t('admin.users.addUser')}
      </h4>
      <p className="text-xs text-muted">{t('admin.users.addUserHint')}</p>
      <div className="flex flex-wrap items-center gap-3">
        <form.Field name="upn">
          {(field) => (
            <UserSearchCombobox
              value={field.state.value}
              onValueChange={field.handleChange}
              onSelect={(user) => field.handleChange(user.userPrincipalName)}
              placeholder={t('admin.users.upnPlaceholder')}
              className="min-w-[220px] flex-1"
              inputClassName="text-xs px-2 py-1.5 w-full"
              autoFocus
            />
          )}
        </form.Field>
        <form.Field name="role">
          {(field) => (
            <select
              aria-label={t('admin.users.colRole')}
              value={field.state.value}
              onChange={(e) => field.handleChange(e.target.value)}
              onBlur={field.handleBlur}
              className="max-w-[180px] cursor-pointer px-2 py-1.5 text-xs"
            >
              <RoleOptions />
            </select>
          )}
        </form.Field>
        <form.Subscribe
          selector={(state) => state.values.upn.trim().length > 0}
        >
          {(upnEntered) => (
            <button
              type="submit"
              className="btn-blue disabled:cursor-not-allowed disabled:opacity-40"
              disabled={isCreating || !upnEntered}
            >
              {isCreating
                ? t('admin.users.creating')
                : t('admin.users.createSubmit')}
            </button>
          )}
        </form.Subscribe>
      </div>
      <ConfirmAdminDialog
        open={confirmAdmin}
        userLabel={form.state.values.upn.trim()}
        onConfirm={() => {
          setConfirmAdmin(false);
          void create(form.state.values);
        }}
        onCancel={() => setConfirmAdmin(false)}
      />
    </form>
  );
}

function sortIndicator(active: boolean, dir: 'asc' | 'desc') {
  if (!active) {
    return '↕';
  }
  if (dir === 'asc') {
    return '▲';
  }
  return '▼';
}

function SortableTh({
  field,
  label,
  query,
  onSort,
}: {
  field: AppUserSortField;
  label: string;
  query: UsersQuery;
  onSort: (field: AppUserSortField) => void;
}) {
  const active = query.sortBy === field;
  return (
    <th>
      <button
        type="button"
        onClick={() => onSort(field)}
        className="inline-flex cursor-pointer items-center gap-1 tracking-[inherit] text-inherit [text-transform:inherit] select-none [font:inherit]"
      >
        {label}
        <span
          aria-hidden
          style={{ color: active ? 'var(--ink)' : 'var(--muted-soft)' }}
        >
          {sortIndicator(active, query.sortDir)}
        </span>
      </button>
    </th>
  );
}

/** Clicking the sorted column flips it; a new column starts newest-first for
 *  dates and A→Z for the rest. Any re-sort goes back to page 1. */
function sortPatch(
  query: UsersQuery,
  field: AppUserSortField,
): Partial<UsersQuery> {
  if (query.sortBy === field) {
    const sortDir = query.sortDir === 'asc' ? 'desc' : 'asc';
    return { sortDir, page: 1 };
  }
  const sortDir =
    field === 'createdAt' || field === 'updatedAt' ? 'desc' : 'asc';
  return { sortBy: field, sortDir, page: 1 };
}

type AppUserRow = AppUsersPage['users'][number];

type UpdateUser = (
  oid: string,
  body: { role?: AppRole | null; active?: boolean },
) => void;

export function UsersTab({
  data,
  isLoading,
  query,
  onQueryChange,
  onUpdate,
  onCreate,
  isCreating,
}: {
  data: AppUsersPage | undefined;
  isLoading: boolean;
  query: UsersQuery;
  onQueryChange: (patch: Partial<UsersQuery>) => void;
  onUpdate: UpdateUser;
  onCreate: (body: { upn: string; role?: AppRole | null }) => Promise<unknown>;
  isCreating: boolean;
}) {
  const { t } = useTranslation();
  const [showAddForm, setShowAddForm] = useState(false);
  // The row whose <select> picked 'admin', held until the dialog decides. The
  // select is controlled by server data, so cancelling simply never applies.
  const [pendingAdmin, setPendingAdmin] = useState<{
    oid: string;
    label: string;
  } | null>(null);

  const handleSort = (field: AppUserSortField) => {
    onQueryChange(sortPatch(query, field));
  };

  const users = data?.users ?? [];
  const totalPages = data ? Math.ceil(data.total / data.limit) : 0;

  return (
    <div>
      <div className="mb-5 flex items-end justify-between">
        <div>
          <p className="label dash">{t('admin.users.title')}</p>
          {data && (
            <p className="mt-1 text-sm text-muted">
              {t('admin.users.count', { count: data.total })}
            </p>
          )}
        </div>
        <AddUserToggle
          open={showAddForm}
          onToggle={() => setShowAddForm((v) => !v)}
        />
      </div>

      {showAddForm && (
        <AddUserForm
          onCreate={onCreate}
          isCreating={isCreating}
          onClose={() => setShowAddForm(false)}
        />
      )}

      <UsersSearchForm q={query.q} onQueryChange={onQueryChange} />

      {isLoading ? (
        <LoadingSpinner />
      ) : (
        <>
          {users.length === 0 ? (
            <UsersEmpty searching={Boolean(query.q)} />
          ) : (
            <UsersTable
              users={users}
              query={query}
              onSort={handleSort}
              onUpdate={onUpdate}
              onRequestAdmin={setPendingAdmin}
            />
          )}

          {totalPages > 1 && (
            <UsersPager
              page={query.page}
              totalPages={totalPages}
              onQueryChange={onQueryChange}
            />
          )}
        </>
      )}
      <ConfirmAdminDialog
        open={pendingAdmin !== null}
        userLabel={pendingAdmin?.label ?? ''}
        onConfirm={() => {
          if (pendingAdmin) {
            onUpdate(pendingAdmin.oid, { role: 'admin' });
          }
          setPendingAdmin(null);
        }}
        onCancel={() => setPendingAdmin(null)}
      />
    </div>
  );
}

function AddUserToggle({
  open,
  onToggle,
}: {
  open: boolean;
  onToggle: () => void;
}) {
  const { t } = useTranslation();
  return (
    <button
      type="button"
      onClick={onToggle}
      className={open ? 'btn-ghost' : 'btn-blue'}
    >
      {open ? (
        t('admin.users.cancel')
      ) : (
        <>
          <svg
            className="size-3.5"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 4v16m8-8H4"
            />
          </svg>
          {t('admin.users.addUser')}
        </>
      )}
    </button>
  );
}

function UsersSearchForm({
  q,
  onQueryChange,
}: {
  q: string;
  onQueryChange: (patch: Partial<UsersQuery>) => void;
}) {
  const { t } = useTranslation();
  const searchForm = useForm({
    defaultValues: { q },
    onSubmit: ({ value }) => {
      onQueryChange({ q: value.q.trim(), page: 1 });
    },
  });

  // The URL is the source of truth (back/forward navigation, shared links) —
  // reset the input when it changes underneath us. Remounting on a `key` would
  // be the tidier shape, but it would also blur the input mid-search, so this
  // one stays an effect.
  //
  // `keepDefaultValues` is load-bearing: a plain `reset(values)` rewrites the
  // form's own defaultValues, and `useForm` re-applies the ones passed to it
  // after *every* render — so the next render would compare the two, find them
  // different, see the freshly-reset (hence untouched) form, and put the
  // original defaults straight back over this.
  useEffect(() => {
    searchForm.reset({ q }, { keepDefaultValues: true });
  }, [q, searchForm]);

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        void searchForm.handleSubmit();
      }}
      className="mb-5 flex flex-wrap gap-2"
    >
      <searchForm.Field name="q">
        {(field) => (
          <input
            type="search"
            placeholder={t('admin.users.searchPlaceholder')}
            value={field.state.value}
            onChange={(e) => field.handleChange(e.target.value)}
            onBlur={field.handleBlur}
            className="min-w-36 flex-1 px-3 py-1.5 text-xs"
          />
        )}
      </searchForm.Field>
      <button type="submit" className="btn-blue">
        {t('admin.users.search')}
      </button>
    </form>
  );
}

function UsersEmpty({ searching }: { searching: boolean }) {
  const { t } = useTranslation();
  return (
    <div className="py-16 text-center">
      <p className="mono text-[11px] text-muted-soft">
        {searching ? t('admin.users.noResults') : t('admin.users.noUsers')}
      </p>
    </div>
  );
}

function UsersTable({
  users,
  query,
  onSort,
  onUpdate,
  onRequestAdmin,
}: {
  users: AppUserRow[];
  query: UsersQuery;
  onSort: (field: AppUserSortField) => void;
  onUpdate: UpdateUser;
  onRequestAdmin: (pending: { oid: string; label: string }) => void;
}) {
  const { t } = useTranslation();
  return (
    <div className="overflow-x-auto border-y border-ink">
      <table className="epi-table">
        <thead>
          <tr>
            <SortableTh
              field="displayName"
              label={t('admin.users.colUser')}
              query={query}
              onSort={onSort}
            />
            <SortableTh
              field="role"
              label={t('admin.users.colRole')}
              query={query}
              onSort={onSort}
            />
            <SortableTh
              field="active"
              label={t('admin.users.colStatus')}
              query={query}
              onSort={onSort}
            />
            <SortableTh
              field="createdAt"
              label={t('admin.users.colSince')}
              query={query}
              onSort={onSort}
            />
          </tr>
        </thead>
        <tbody>
          {users.map((u) => (
            <UserRow
              key={u.oid}
              user={u}
              onUpdate={onUpdate}
              onRequestAdmin={onRequestAdmin}
            />
          ))}
        </tbody>
      </table>
    </div>
  );
}

function UserRow({
  user: u,
  onUpdate,
  onRequestAdmin,
}: {
  user: AppUserRow;
  onUpdate: UpdateUser;
  onRequestAdmin: (pending: { oid: string; label: string }) => void;
}) {
  const { t } = useTranslation();
  const statusColor = u.active ? 'var(--ink)' : 'var(--muted-soft)';
  const statusBorder = u.active ? 'var(--ink)' : 'var(--rule)';
  const statusLabel = u.active
    ? t('admin.users.active')
    : t('admin.users.disabled');
  return (
    <tr>
      <td>
        <p className="max-w-xs truncate font-medium">
          {u.displayName ?? t('admin.users.noName')}
        </p>
        <p className="mono max-w-xs truncate text-xs text-muted">
          {u.upn ?? u.oid}
        </p>
      </td>
      <td>
        <select
          aria-label={t('admin.users.roleOf', {
            user: u.displayName ?? u.upn ?? u.oid,
          })}
          value={u.role ?? ''}
          onChange={(e) => {
            const role = roleValue(e.target.value);
            if (role === 'admin') {
              onRequestAdmin({
                oid: u.oid,
                label: u.displayName ?? u.upn ?? u.oid,
              });
              return;
            }
            onUpdate(u.oid, { role });
          }}
          className="w-full max-w-[180px] cursor-pointer px-2 py-1.5 text-xs"
        >
          <RoleOptions />
        </select>
      </td>
      <td>
        <label className="flex w-fit cursor-pointer items-center gap-2 select-none">
          <input
            type="checkbox"
            checked={u.active}
            onChange={(e) => {
              onUpdate(u.oid, { active: e.target.checked });
            }}
            className="size-4"
          />
          <span
            className="epi-tag"
            style={{ color: statusColor, borderColor: statusBorder }}
          >
            {statusLabel}
          </span>
        </label>
      </td>
      <td className="mono text-xs text-muted">{fmt(u.createdAt)}</td>
    </tr>
  );
}

function UsersPager({
  page,
  totalPages,
  onQueryChange,
}: {
  page: number;
  totalPages: number;
  onQueryChange: (patch: Partial<UsersQuery>) => void;
}) {
  const { t } = useTranslation();
  const onFirst = page === 1;
  const onLast = page === totalPages;
  return (
    <div className="flex items-center justify-center gap-3 pt-4">
      <button
        type="button"
        onClick={() => onQueryChange({ page: Math.max(page - 1, 1) })}
        disabled={onFirst}
        className="btn-link"
        style={{ opacity: onFirst ? 0.4 : 1 }}
      >
        {t('admin.users.prev')}
      </button>
      <span className="label">
        {t('admin.users.page', { page, total: totalPages })}
      </span>
      <button
        type="button"
        onClick={() => onQueryChange({ page: Math.min(page + 1, totalPages) })}
        disabled={onLast}
        className="btn-link"
        style={{ opacity: onLast ? 0.4 : 1 }}
      >
        {t('admin.users.next')}
      </button>
    </div>
  );
}
